import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Palette, RotateCcw, Eye, EyeOff } from 'lucide-react';

interface FilterSettings {
  brightness: number;
  contrast: number;
  saturation: number;
  hue: number;
  blur: number;
  opacity: number;
}

type OutputFormat = 'png' | 'jpg';

interface FilterPreset {
  name: string;
  settings: FilterSettings;
}

const DEFAULT_FILTERS: FilterSettings = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hue: 0,
  blur: 0,
  opacity: 100,
};

const PRESETS: FilterPreset[] = [
  { name: 'Original', settings: { brightness: 100, contrast: 100, saturation: 100, hue: 0, blur: 0, opacity: 100 } },
  { name: 'Vintage', settings: { brightness: 110, contrast: 85, saturation: 60, hue: 15, blur: 0.5, opacity: 100 } },
  { name: 'Warm', settings: { brightness: 105, contrast: 105, saturation: 120, hue: 10, blur: 0, opacity: 100 } },
  { name: 'Cool', settings: { brightness: 100, contrast: 110, saturation: 90, hue: 200, blur: 0, opacity: 100 } },
  { name: 'B&W', settings: { brightness: 100, contrast: 120, saturation: 0, hue: 0, blur: 0, opacity: 100 } },
  { name: 'Sepia', settings: { brightness: 100, contrast: 90, saturation: 30, hue: 30, blur: 0, opacity: 100 } },
  { name: 'High Contrast', settings: { brightness: 100, contrast: 180, saturation: 110, hue: 0, blur: 0, opacity: 100 } },
  { name: 'Vivid', settings: { brightness: 110, contrast: 130, saturation: 180, hue: 0, blur: 0, opacity: 100 } },
  { name: 'Fade', settings: { brightness: 120, contrast: 80, saturation: 70, hue: 0, blur: 0.3, opacity: 100 } },
  { name: 'Dramatic', settings: { brightness: 90, contrast: 160, saturation: 80, hue: 0, blur: 0, opacity: 100 } },
];

const SLIDERS = [
  { key: 'brightness' as const, label: 'Brightness', min: 0, max: 200, unit: '%' },
  { key: 'contrast' as const, label: 'Contrast', min: 0, max: 200, unit: '%' },
  { key: 'saturation' as const, label: 'Saturation', min: 0, max: 200, unit: '%' },
  { key: 'hue' as const, label: 'Hue Rotate', min: 0, max: 360, unit: 'deg' },
  { key: 'blur' as const, label: 'Blur', min: 0, max: 10, unit: 'px', step: 0.1 },
  { key: 'opacity' as const, label: 'Opacity', min: 50, max: 100, unit: '%' },
];

const PhotoFilters: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterSettings>({ ...DEFAULT_FILTERS });
  const [activePreset, setActivePreset] = useState<string>('Original');
  const [showOriginal, setShowOriginal] = useState(false);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const thumbnailRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    setFilters({ ...DEFAULT_FILTERS });
    setActivePreset('Original');
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const getFilterString = useCallback((f: FilterSettings) => {
    return `brightness(${f.brightness}%) contrast(${f.contrast}%) saturate(${f.saturation}%) hue-rotate(${f.hue}deg) blur(${f.blur}px) opacity(${f.opacity}%)`;
  }, []);

  // Render preset thumbnails
  useEffect(() => {
    if (!imgElement) return;
    const thumbSize = 80;
    const aspect = imgElement.naturalWidth / imgElement.naturalHeight;
    const tw = aspect >= 1 ? thumbSize : Math.round(thumbSize * aspect);
    const th = aspect >= 1 ? Math.round(thumbSize / aspect) : thumbSize;

    PRESETS.forEach((preset) => {
      const canvas = thumbnailRefs.current.get(preset.name);
      if (!canvas) return;
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.filter = getFilterString(preset.settings);
      ctx.drawImage(imgElement, 0, 0, tw, th);
    });
  }, [imgElement, getFilterString]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    const canvas = previewCanvasRef.current;
    canvas.width = imgElement.naturalWidth;
    canvas.height = imgElement.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (showOriginal) {
      ctx.filter = 'none';
    } else {
      ctx.filter = getFilterString(filters);
    }
    ctx.drawImage(imgElement, 0, 0);
  }, [imgElement, filters, showOriginal, getFilterString]);

  const applyPreset = useCallback((preset: FilterPreset) => {
    setFilters({ ...preset.settings });
    setActivePreset(preset.name);
  }, []);

  const downloadResult = useCallback(() => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = imgElement.naturalWidth;
    canvas.height = imgElement.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.filter = getFilterString(filters);
    ctx.drawImage(imgElement, 0, 0);

    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      const ext = outputFormat === 'jpg' ? 'jpg' : 'png';
      link.download = `${name}_filtered.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  }, [imgElement, file, filters, getFilterString, outputFormat, jpgQuality]);

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setActivePreset('Original');
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setFilters({ ...DEFAULT_FILTERS });
    setActivePreset('Original');
    setShowOriginal(false);
    setOutputFormat('png');
    setJpgQuality(92);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const setThumbnailRef = useCallback((name: string, el: HTMLCanvasElement | null) => {
    if (el) {
      thumbnailRefs.current.set(name, el);
    }
  }, []);

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-amber-100 rounded-full">
            <Palette className="w-8 h-8 text-amber-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Photo Filters</h2>
        <p className="text-gray-600">
          Apply stunning preset filters or fine-tune with manual adjustments.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-amber-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Filter Presets */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Filter Presets</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                    activePreset === preset.name
                      ? 'border-amber-500 bg-amber-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-amber-300'
                  }`}
                >
                  <div className="w-full aspect-square bg-gray-200 rounded overflow-hidden flex items-center justify-center">
                    <canvas
                      ref={(el) => setThumbnailRef(preset.name, el)}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    activePreset === preset.name ? 'text-amber-700' : 'text-gray-600'
                  }`}>
                    {preset.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Manual Adjustments */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Manual Adjustments</label>
              <button
                onClick={resetFilters}
                className="text-xs text-amber-600 hover:text-amber-700 font-medium"
              >
                Reset to Defaults
              </button>
            </div>
            {SLIDERS.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-600">{s.label}</label>
                  <span className="text-xs font-bold text-gray-900">
                    {s.step ? filters[s.key].toFixed(1) : filters[s.key]}{s.unit}
                  </span>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step || 1}
                  value={filters[s.key]}
                  onChange={(e) => {
                    const val = s.step ? parseFloat(e.target.value) : parseInt(e.target.value);
                    setFilters((prev) => ({ ...prev, [s.key]: val }));
                    setActivePreset('');
                  }}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                />
              </div>
            ))}
          </div>

          {/* Output Format */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {(['png', 'jpg'] as OutputFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium uppercase transition-colors ${
                      outputFormat === fmt
                        ? 'bg-amber-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-amber-300'
                    }`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-medium text-gray-600">JPG Quality</label>
                    <span className="text-xs font-bold text-gray-900">{jpgQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Preview</h3>
              <button
                onClick={() => setShowOriginal((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
              >
                {showOriginal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showOriginal ? 'Show Filtered' : 'Show Original'}
              </button>
            </div>
            <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
              <canvas ref={previewCanvasRef} className="max-w-full max-h-[500px] object-contain" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Filtered Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoFilters;
